import React, { useState, Component } from 'react';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { BuildingIcon, SearchIcon, FilterIcon, ChevronDownIcon, MapPinIcon, UsersIcon, GraduationCapIcon, BarChart3Icon, AlertCircleIcon, CheckCircleIcon, XCircleIcon, PlusCircleIcon, DownloadIcon, EyeIcon, MoreHorizontalIcon, CalendarIcon, ClockIcon, ArrowUpDownIcon } from 'lucide-react';
// Sample university data
const universitiesData = [{
  id: 1,
  name: 'UAE Business School',
  location: 'Dubai',
  programs: 12,
  activeApplications: 48,
  pendingReviews: 22,
  healthScore: 92,
  status: 'Active',
  lastUpdated: '2023-06-15',
  establishedYear: 1997,
  studentsEnrolled: 4850,
  accreditation: 'Ministry of Education, UAE',
  intakePeriods: ['Fall 2023', 'Spring 2024'],
  programTypes: ["Bachelor's", "Master's", 'Doctorate'],
  flagged: false
}, {
  id: 2,
  name: 'Emirates Institute of Technology',
  location: 'Abu Dhabi',
  programs: 24,
  activeApplications: 103,
  pendingReviews: 56,
  healthScore: 87,
  status: 'Active',
  lastUpdated: '2023-06-12',
  establishedYear: 2001,
  studentsEnrolled: 6200,
  accreditation: 'Ministry of Education, UAE',
  intakePeriods: ['Fall 2023', 'Spring 2024', 'Summer 2024'],
  programTypes: ["Bachelor's", "Master's", 'Diploma'],
  flagged: false
}, {
  id: 3,
  name: 'Sharjah American University',
  location: 'Sharjah',
  programs: 18,
  activeApplications: 75,
  pendingReviews: 31,
  healthScore: 94,
  status: 'Active',
  lastUpdated: '2023-06-10',
  establishedYear: 1995,
  studentsEnrolled: 5100,
  accreditation: 'Ministry of Education, UAE',
  intakePeriods: ['Fall 2023', 'Spring 2024'],
  programTypes: ["Bachelor's", "Master's", 'Doctorate'],
  flagged: false
}, {
  id: 4,
  name: 'Dubai Medical College',
  location: 'Dubai',
  programs: 8,
  activeApplications: 62,
  pendingReviews: 15,
  healthScore: 89,
  status: 'Under Review',
  lastUpdated: '2023-06-05',
  establishedYear: 2003,
  studentsEnrolled: 2350,
  accreditation: 'Ministry of Education, UAE',
  intakePeriods: ['Fall 2023'],
  programTypes: ["Bachelor's", "Master's"],
  flagged: true
}, {
  id: 5,
  name: 'Abu Dhabi School of Management',
  location: 'Abu Dhabi',
  programs: 10,
  activeApplications: 42,
  pendingReviews: 18,
  healthScore: 81,
  status: 'Active',
  lastUpdated: '2023-06-08',
  establishedYear: 2006,
  studentsEnrolled: 1850,
  accreditation: 'Ministry of Education, UAE',
  intakePeriods: ['Fall 2023', 'Spring 2024'],
  programTypes: ["Bachelor's", "Master's"],
  flagged: true
}, {
  id: 6,
  name: 'Zayed University',
  location: 'Abu Dhabi',
  programs: 28,
  activeApplications: 118,
  pendingReviews: 43,
  healthScore: 95,
  status: 'Active',
  lastUpdated: '2023-06-07',
  establishedYear: 1998,
  studentsEnrolled: 8500,
  accreditation: 'Ministry of Education, UAE',
  intakePeriods: ['Fall 2023', 'Spring 2024', 'Summer 2024'],
  programTypes: ["Bachelor's", "Master's", 'Doctorate'],
  flagged: false
}, {
  id: 7,
  name: 'Ajman University',
  location: 'Ajman',
  programs: 16,
  activeApplications: 68,
  pendingReviews: 24,
  healthScore: 88,
  status: 'Active',
  lastUpdated: '2023-06-09',
  establishedYear: 1999,
  studentsEnrolled: 4200,
  accreditation: 'Ministry of Education, UAE',
  intakePeriods: ['Fall 2023', 'Spring 2024'],
  programTypes: ["Bachelor's", "Master's"],
  flagged: false
}, {
  id: 8,
  name: 'RAK Medical & Health Sciences University',
  location: 'Ras Al Khaimah',
  programs: 6,
  activeApplications: 45,
  pendingReviews: 12,
  healthScore: 85,
  status: 'Under Review',
  lastUpdated: '2023-06-03',
  establishedYear: 2006,
  studentsEnrolled: 1200,
  accreditation: 'Ministry of Education, UAE',
  intakePeriods: ['Fall 2023'],
  programTypes: ["Bachelor's", "Master's"],
  flagged: true
}];
// University Status Badge Component
const StatusBadge = ({
  status
}) => {
  const getStatusColor = () => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Under Review':
        return 'bg-yellow-100 text-yellow-800';
      case 'Inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor()}`} data-id="element-1885">
      {status}
    </span>;
};
// Health Score Component
const HealthScore = ({
  score
}) => {
  const getScoreColor = () => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-blue-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };
  return <div className="flex items-center" data-id="element-1886">
      <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden mr-2" data-id="element-1887">
        <div className={`h-full ${score >= 90 ? 'bg-green-500' : score >= 80 ? 'bg-blue-500' : score >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
        width: `${score}%`
      }} data-id="element-1888"></div>
      </div>
      <span className={`text-sm font-medium ${getScoreColor()}`} data-id="element-1889">{score}</span>
    </div>;
};
// University Detail Modal Component
const UniversityDetailModal = ({
  isOpen,
  onClose,
  university
}) => {
  if (!isOpen || !university) return null;
  return <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="university-detail-modal" role="dialog" aria-modal="true" data-id="element-1890">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-1891">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose} data-id="element-1892"></div>
        {/* Modal positioning */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-1893">
          &#8203;
        </span>
        {/* Modal content */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full" data-id="element-1894">
          <div className="bg-primary-400 px-6 py-4 flex justify-between items-center" data-id="element-1895">
            <h3 className="text-lg font-medium text-white" id="modal-title" data-id="element-1896">
              University Details
            </h3>
            <button onClick={onClose} className="text-white hover:text-gray-200" data-id="element-1897">
              <XCircleIcon className="h-6 w-6" data-id="element-1898" />
            </button>
          </div>
          <div className="bg-white px-6 py-4" data-id="element-1899">
            <div className="flex items-center mb-6" data-id="element-1900">
              <div className="h-16 w-16 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mr-4" data-id="element-1901">
                <BuildingIcon className="h-8 w-8" data-id="element-1902" />
              </div>
              <div data-id="element-1903">
                <h2 className="text-xl font-semibold text-gray-900" data-id="element-1904">
                  {university.name}
                </h2>
                <div className="flex items-center mt-1" data-id="element-1905">
                  <MapPinIcon className="h-4 w-4 text-gray-500 mr-1" data-id="element-1906" />
                  <span className="text-gray-600" data-id="element-1907">{university.location}</span>
                  <span className="mx-2 text-gray-300" data-id="element-1908">•</span>
                  <StatusBadge status={university.status} data-id="element-1909" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-1910">
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1911">
                <div className="text-sm text-gray-500 mb-1" data-id="element-1912">Established</div>
                <div className="text-lg font-medium" data-id="element-1913">
                  {university.establishedYear}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1914">
                <div className="text-sm text-gray-500 mb-1" data-id="element-1915">
                  Students Enrolled
                </div>
                <div className="text-lg font-medium" data-id="element-1916">
                  {university.studentsEnrolled.toLocaleString()}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1917">
                <div className="text-sm text-gray-500 mb-1" data-id="element-1918">Health Score</div>
                <div className="text-lg font-medium flex items-center" data-id="element-1919">
                  <HealthScore score={university.healthScore} data-id="element-1920" />
                </div>
              </div>
            </div>
            <div className="mb-6" data-id="element-1921">
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1922">
                Programs & Applications
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1923">
                <div className="border border-gray-200 rounded-md p-4" data-id="element-1924">
                  <div className="flex items-center" data-id="element-1925">
                    <div className="p-2 bg-primary-50 rounded-full mr-3" data-id="element-1926">
                      <GraduationCapIcon className="h-5 w-5 text-primary-500" data-id="element-1927" />
                    </div>
                    <div data-id="element-1928">
                      <div className="text-2xl font-semibold" data-id="element-1929">
                        {university.programs}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-1930">Programs</div>
                    </div>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-md p-4" data-id="element-1931">
                  <div className="flex items-center" data-id="element-1932">
                    <div className="p-2 bg-primary-50 rounded-full mr-3" data-id="element-1933">
                      <UsersIcon className="h-5 w-5 text-primary-500" data-id="element-1934" />
                    </div>
                    <div data-id="element-1935">
                      <div className="text-2xl font-semibold" data-id="element-1936">
                        {university.activeApplications}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-1937">Applications</div>
                    </div>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-md p-4" data-id="element-1938">
                  <div className="flex items-center" data-id="element-1939">
                    <div className="p-2 bg-yellow-50 rounded-full mr-3" data-id="element-1940">
                      <ClockIcon className="h-5 w-5 text-yellow-500" data-id="element-1941" />
                    </div>
                    <div data-id="element-1942">
                      <div className="text-2xl font-semibold" data-id="element-1943">
                        {university.pendingReviews}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-1944">
                        Pending Reviews
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-6" data-id="element-1945">
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1946">
                Intake Periods
              </h4>
              <div className="flex flex-wrap gap-2" data-id="element-1947">
                {university.intakePeriods.map((intake, index) => <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-50 text-primary-700" data-id="element-1948">
                    <CalendarIcon className="w-4 h-4 mr-1" data-id="element-1949" />
                    {intake}
                  </span>)}
              </div>
            </div>
            <div className="mb-6" data-id="element-1950">
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1951">
                Program Types
              </h4>
              <div className="flex flex-wrap gap-2" data-id="element-1952">
                {university.programTypes.map((type, index) => <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-50 text-blue-700" data-id="element-1953">
                    <GraduationCapIcon className="w-4 h-4 mr-1" data-id="element-1954" />
                    {type}
                  </span>)}
              </div>
            </div>
            <div className="mb-6" data-id="element-1955">
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1956">
                Accreditation
              </h4>
              <div className="text-gray-700" data-id="element-1957">{university.accreditation}</div>
            </div>
            {university.flagged && <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg" data-id="element-1958">
                <div className="flex items-start" data-id="element-1959">
                  <AlertCircleIcon className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-1960" />
                  <div data-id="element-1961">
                    <h3 className="font-medium text-red-800 mb-1" data-id="element-1962">
                      Flagged for Review
                    </h3>
                    <p className="text-sm text-red-700" data-id="element-1963">
                      This university has been flagged for review due to
                      multiple programs with high skill gaps and declining
                      application trends.
                    </p>
                  </div>
                </div>
              </div>}
          </div>
          <div className="bg-gray-50 px-6 py-4 flex justify-between" data-id="element-1964">
            <button className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100" onClick={onClose} data-id="element-1965">
              Close
            </button>
            <div className="flex space-x-2" data-id="element-1966">
              <button className="px-4 py-2 border border-primary-300 bg-primary-50 rounded-md text-sm font-medium text-primary-700 hover:bg-primary-100" data-id="element-1967">
                View Programs
              </button>
              <button className="px-4 py-2 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600" data-id="element-1968">
                View Analytics
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
const Universities = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  // Filter universities based on search query and filters
  const filteredUniversities = universitiesData.filter(university => university.name.toLowerCase().includes(searchQuery.toLowerCase()) || university.location.toLowerCase().includes(searchQuery.toLowerCase())).filter(university => statusFilter === 'all' || university.status === statusFilter).filter(university => locationFilter === 'all' || university.location === locationFilter).sort((a, b) => {
    const factor = sortOrder === 'asc' ? 1 : -1;
    switch (sortBy) {
      case 'name':
        return factor * a.name.localeCompare(b.name);
      case 'location':
        return factor * a.location.localeCompare(b.location);
      case 'programs':
        return factor * (a.programs - b.programs);
      case 'applications':
        return factor * (a.activeApplications - b.activeApplications);
      case 'healthScore':
        return factor * (a.healthScore - b.healthScore);
      default:
        return 0;
    }
  });
  // Get unique locations for filter
  const locations = [...new Set(universitiesData.map(uni => uni.location))];
  // Handle sort toggle
  const toggleSort = field => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };
  // Open university detail modal
  const openDetailModal = university => {
    setSelectedUniversity(university);
    setIsDetailModalOpen(true);
  };
  // Close university detail modal
  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedUniversity(null);
  };
  // Navigate to university detail page
  const navigateToUniversityDetail = universityId => {
    navigate(`/admin/universities/${universityId}`);
  };
  return <Layout title="Universities Management" persona="Platform Admin" data-id="element-1969">
      <div className="space-y-6" data-id="element-1970">
        {/* Action Bar */}
        <div className="flex flex-wrap justify-between items-center gap-4" data-id="element-1971">
          <div className="flex-grow max-w-2xl" data-id="element-1972">
            <div className="relative" data-id="element-1973">
              <input type="text" placeholder="Search universities by name or location..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-1974" />
              <SearchIcon className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" data-id="element-1975" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3" data-id="element-1976">
            {/* Status Filter */}
            <div className="relative" data-id="element-1977">
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="pl-3 pr-8 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-1978">
                <option value="all" data-id="element-1979">All Statuses</option>
                <option value="Active" data-id="element-1980">Active</option>
                <option value="Under Review" data-id="element-1981">Under Review</option>
                <option value="Inactive" data-id="element-1982">Inactive</option>
              </select>
              <ChevronDownIcon className="absolute right-3 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" data-id="element-1983" />
            </div>
            {/* Location Filter */}
            <div className="relative" data-id="element-1984">
              <select value={locationFilter} onChange={e => setLocationFilter(e.target.value)} className="pl-3 pr-8 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-1985">
                <option value="all" data-id="element-1986">All Locations</option>
                {locations.map((location, index) => <option key={index} value={location} data-id="element-1987">
                    {location}
                  </option>)}
              </select>
              <ChevronDownIcon className="absolute right-3 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" data-id="element-1988" />
            </div>
            {/* Export Button */}
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-1989">
              <DownloadIcon className="h-4 w-4 mr-2 text-gray-500" data-id="element-1990" />
              Export
            </button>
            {/* Add University Button */}
            <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600" data-id="element-1991">
              <PlusCircleIcon className="h-4 w-4 mr-2" data-id="element-1992" />
              Add University
            </button>
          </div>
        </div>

        {/* Universities Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-1993">
          <div className="overflow-x-auto" data-id="element-1994">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1995">
              <thead className="bg-gray-50" data-id="element-1996">
                <tr data-id="element-1997">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('name')} data-id="element-1998">
                    <div className="flex items-center" data-id="element-1999">
                      University
                      {sortBy === 'name' && <ArrowUpDownIcon className="w-4 h-4 ml-1" data-id="element-2000" />}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('location')} data-id="element-2001">
                    <div className="flex items-center" data-id="element-2002">
                      Location
                      {sortBy === 'location' && <ArrowUpDownIcon className="w-4 h-4 ml-1" data-id="element-2003" />}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('programs')} data-id="element-2004">
                    <div className="flex items-center" data-id="element-2005">
                      Programs
                      {sortBy === 'programs' && <ArrowUpDownIcon className="w-4 h-4 ml-1" data-id="element-2006" />}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('applications')} data-id="element-2007">
                    <div className="flex items-center" data-id="element-2008">
                      Applications
                      {sortBy === 'applications' && <ArrowUpDownIcon className="w-4 h-4 ml-1" data-id="element-2009" />}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('healthScore')} data-id="element-2010">
                    <div className="flex items-center" data-id="element-2011">
                      Health Score
                      {sortBy === 'healthScore' && <ArrowUpDownIcon className="w-4 h-4 ml-1" data-id="element-2012" />}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2013">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2014">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-2015">
                {filteredUniversities.map(university => <tr key={university.id} className={`hover:bg-gray-50 cursor-pointer ${university.flagged ? 'bg-red-50' : ''}`} onClick={() => navigateToUniversityDetail(university.id)} data-id="element-2016">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2017">
                      <div className="flex items-center" data-id="element-2018">
                        <div className="flex-shrink-0 h-10 w-10 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center" data-id="element-2019">
                          <BuildingIcon className="h-5 w-5" data-id="element-2020" />
                        </div>
                        <div className="ml-4" data-id="element-2021">
                          <div className="text-sm font-medium text-gray-900 flex items-center" data-id="element-2022">
                            {university.name}
                            {university.flagged && <AlertCircleIcon className="h-4 w-4 text-red-500 ml-1" data-id="element-2023" />}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-2024">
                            Updated {university.lastUpdated}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2025">
                      <div className="flex items-center text-sm text-gray-900" data-id="element-2026">
                        <MapPinIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-2027" />
                        {university.location}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2028">
                      <div className="text-sm text-gray-500" data-id="element-2029">
                        {university.programs}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2030">
                      <div className="text-sm text-gray-900" data-id="element-2031">
                        {university.activeApplications}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-2032">
                        {university.pendingReviews} pending
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2033">
                      <HealthScore score={university.healthScore} data-id="element-2034" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2035">
                      <StatusBadge status={university.status} data-id="element-2036" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2037">
                      <div className="flex justify-end space-x-2" data-id="element-2038">
                        <button onClick={e => {
                      e.stopPropagation(); // Prevent row click from triggering
                      navigateToUniversityDetail(university.id);
                    }} className="text-gray-600 hover:text-gray-900" data-id="element-2039">
                          
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          {filteredUniversities.length === 0 && <div className="text-center py-12" data-id="element-2040">
              <BuildingIcon className="mx-auto h-12 w-12 text-gray-300" data-id="element-2041" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-2042">
                No universities found
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-2043">
                Try adjusting your search or filter criteria
              </p>
            </div>}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2044">
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2045">
            <div className="flex items-center justify-between mb-4" data-id="element-2046">
              <h3 className="font-medium text-gray-800" data-id="element-2047">Status Summary</h3>
              <CheckCircleIcon className="w-5 h-5 text-gray-400" data-id="element-2048" />
            </div>
            <div className="space-y-3" data-id="element-2049">
              <div data-id="element-2050">
                <div className="flex items-center justify-between mb-1" data-id="element-2051">
                  <div className="text-sm" data-id="element-2052">Active</div>
                  <div className="text-sm font-medium" data-id="element-2053">
                    {universitiesData.filter(u => u.status === 'Active').length}
                  </div>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-2054">
                  <div className="h-full bg-green-500" style={{
                  width: `${universitiesData.filter(u => u.status === 'Active').length / universitiesData.length * 100}%`
                }} data-id="element-2055"></div>
                </div>
              </div>
              <div data-id="element-2056">
                <div className="flex items-center justify-between mb-1" data-id="element-2057">
                  <div className="text-sm" data-id="element-2058">Under Review</div>
                  <div className="text-sm font-medium" data-id="element-2059">
                    {universitiesData.filter(u => u.status === 'Under Review').length}
                  </div>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-2060">
                  <div className="h-full bg-yellow-500" style={{
                  width: `${universitiesData.filter(u => u.status === 'Under Review').length / universitiesData.length * 100}%`
                }} data-id="element-2061"></div>
                </div>
              </div>
              <div data-id="element-2062">
                <div className="flex items-center justify-between mb-1" data-id="element-2063">
                  <div className="text-sm" data-id="element-2064">Inactive</div>
                  <div className="text-sm font-medium" data-id="element-2065">
                    {universitiesData.filter(u => u.status === 'Inactive').length}
                  </div>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-2066">
                  <div className="h-full bg-red-500" style={{
                  width: `${universitiesData.filter(u => u.status === 'Inactive').length / universitiesData.length * 100}%`
                }} data-id="element-2067"></div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2068">
            <div className="flex items-center justify-between mb-4" data-id="element-2069">
              <h3 className="font-medium text-gray-800" data-id="element-2070">
                Location Distribution
              </h3>
              <MapPinIcon className="w-5 h-5 text-gray-400" data-id="element-2071" />
            </div>
            <div className="space-y-3" data-id="element-2072">
              {locations.map((location, index) => {
              const count = universitiesData.filter(u => u.location === location).length;
              const percentage = count / universitiesData.length * 100;
              return <div key={index} data-id="element-2073">
                    <div className="flex items-center justify-between mb-1" data-id="element-2074">
                      <div className="text-sm" data-id="element-2075">{location}</div>
                      <div className="text-sm font-medium" data-id="element-2076">{count}</div>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-2077">
                      <div className="h-full bg-primary-500" style={{
                    width: `${percentage}%`
                  }} data-id="element-2078"></div>
                    </div>
                  </div>;
            })}
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2079">
            <div className="flex items-center justify-between mb-4" data-id="element-2080">
              <h3 className="font-medium text-gray-800" data-id="element-2081">
                Flagged Universities
              </h3>
              <AlertCircleIcon className="w-5 h-5 text-gray-400" data-id="element-2082" />
            </div>
            {universitiesData.filter(u => u.flagged).length > 0 ? <div className="space-y-3" data-id="element-2083">
                {universitiesData.filter(u => u.flagged).map(university => <div key={university.id} className="p-3 bg-red-50 rounded-md flex items-center justify-between cursor-pointer hover:bg-red-100" onClick={() => navigateToUniversityDetail(university.id)} data-id="element-2084">
                      <div className="flex items-center" data-id="element-2085">
                        <AlertCircleIcon className="h-4 w-4 text-red-500 mr-2" data-id="element-2086" />
                        <span className="text-sm text-gray-900" data-id="element-2087">
                          {university.name}
                        </span>
                      </div>
                      <EyeIcon className="h-4 w-4 text-gray-500" data-id="element-2088" />
                    </div>)}
              </div> : <div className="text-center py-8" data-id="element-2089">
                <CheckCircleIcon className="mx-auto h-8 w-8 text-green-500" data-id="element-2090" />
                <p className="mt-2 text-sm text-gray-500" data-id="element-2091">
                  No flagged universities
                </p>
              </div>}
          </div>
        </div>
      </div>

      {/* University Detail Modal */}
      <UniversityDetailModal isOpen={isDetailModalOpen} onClose={closeDetailModal} university={selectedUniversity} data-id="element-2092" />
    </Layout>;
};
export default Universities;